import {
  Alert,
  AlertIcon,
  Box,
  Button,
  CloseButton,
  Flex,
  HStack,
  Image,
  Link,
  Table,
  TableContainer,
  Tag,
  Tbody,
  Td,
  Text,
  Tr,
  VStack,
} from '@chakra-ui/react';
import { ChangeEvent, DragEvent, FC, useEffect, useRef, useState } from 'react';
import { imageToFile } from '../../utils/image-to-file';
import { convertFileSize } from './convert-file-size';
import { IRenderStyle, StyleHandler } from './style-handler';

export interface UploadedFile extends File {
  preview: string;
}

interface FileUploaderProps {
  acceptedTypes?: ReadonlyArray<string>;
  multiple?: boolean;
  maxFiles?: number;
  maxFileSize?: number;
  style?: IRenderStyle['style'];
  message?: string;
  defaultUrlList?: string[];
  isDownloadable?: boolean;
  onUpload?: (files: UploadedFile[]) => void;
  onRemove?: (file: UploadedFile) => void;
  onRemoveAll?: () => void;
}

const withPreview = (file: File): UploadedFile => Object.assign(file, { preview: URL.createObjectURL(file) });

export const FileUploader: FC<FileUploaderProps> = ({
  acceptedTypes = ['*'],
  multiple = true,
  maxFiles = Infinity,
  maxFileSize = 5000000,
  style = 'style_01',
  message,
  defaultUrlList,
  isDownloadable,
  onUpload,
  onRemove,
  onRemoveAll,
}) => {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  async function loadDefaultFiles() {
    if (!defaultUrlList?.length) return;

    try {
      const loaded = await Promise.all(defaultUrlList.map((url) => imageToFile(url)));

      setFiles(loaded.map(withPreview));
    } catch (loadError) {
      console.error(loadError);
    }
  }

  useEffect(() => {
    loadDefaultFiles();
  }, []);

  const clearError = () => {
    setError('');
  };

  useEffect(() => {
    clearError();
  }, [files?.length]);

  const addFiles = (newFiles: File[]) => {
    const uploadedNames = files.map((file) => file.name);
    const duplicates = newFiles.filter((file) => uploadedNames.includes(file.name));

    if (duplicates.length > 0) {
      setError(`The following files are already uploaded: ${duplicates.map((file) => file.name).join(', ')}`);
    } else if (files.length + newFiles.length > maxFiles) {
      setError(`You can upload a maximum of ${maxFiles} files.`);
    } else if (!multiple && files.length > 0) {
      setError("You can't upload more than one file.");
    } else {
      const invalidTypes = newFiles.filter((file) => !acceptedTypes.includes(file.type));

      if (!acceptedTypes.find((type) => type === '*') && invalidTypes.length > 0) {
        setError(`The following files have an invalid type: ${invalidTypes.map((file) => file.name).join(', ')}`);
      } else {
        const tooLarge = newFiles.filter((file) => file.size > maxFileSize);

        if (tooLarge.length > 0) {
          setError(
            `The following files are too large. Maximum file size is ${maxFileSize} bytes: ${tooLarge
              .map((file) => file.name)
              .join(', ')}`
          );
        } else {
          const accepted = newFiles.map(withPreview);

          setFiles((previous) => [...previous, ...accepted]);
          onUpload && onUpload([...files, ...accepted]);
        }
      }
    }
  };

  const handleDragOver = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
  };

  const handleDrop = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    addFiles(Array.from(event.dataTransfer.files));
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(event.target.files ?? []));
  };

  const isAcceptedType = (type: string) => acceptedTypes.includes('*') || acceptedTypes.includes(type);

  const openFilePicker = () => {
    inputRef.current && inputRef.current.click();
  };

  const removeFile = (file: UploadedFile) => {
    if (inputRef?.current) {
      const remaining = new DataTransfer();

      Array.from(inputRef.current.files ?? []).forEach((inputFile) => {
        inputFile !== file && remaining.items.add(inputFile);
      });
      inputRef.current.files = remaining.files;
    }

    setFiles((previous) => previous.filter((uploaded) => uploaded !== file));
    onRemove && onRemove(file);
  };

  const removeAllFiles = () => {
    if (inputRef?.current) {
      inputRef.current.files = new DataTransfer().files;
    }

    setFiles([]);
    onRemoveAll && onRemoveAll();
  };

  const openFile = (file: UploadedFile) => {
    isDownloadable && window.open(file.preview, '_blank');
  };

  return (
    <Box w='full'>
      <input
        type='file'
        accept={acceptedTypes.join(',')}
        multiple={multiple}
        onChange={handleInputChange}
        style={{ display: 'none' }}
        ref={inputRef}
      />
      <VStack
        spacing='md'
        alignItems='stretch'
        className='file-uploader'
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        w='full'>
        <StyleHandler style={style} handleClick={openFilePicker} message={message} />
        {!!error && (
          <Flex justifyContent='space-between' alignItems='center'>
            <Alert status='error' variant='left-accent'>
              <AlertIcon />
              {error}
              <CloseButton ms='auto' onClick={clearError} />
            </Alert>
          </Flex>
        )}
        {!!files.length && (
          <Box>
            <HStack justifyContent='space-between' alignItems='center'>
              <Tag fontWeight='bold' size='lg'>
                Uploaded files:
              </Tag>
              <Button variant='link' colorScheme='red' onClick={removeAllFiles}>
                Remove all
              </Button>
            </HStack>
            <TableContainer mt='md'>
              <Table variant='simple'>
                <Tbody>
                  {files
                    .filter((file) => isAcceptedType(file.type))
                    .map((file) => (
                      <Tr key={file.name}>
                        <Td w='55px' cursor={isDownloadable ? 'pointer' : 'default'} onClick={() => openFile(file)}>
                          {file.type.includes('image') && (
                            <Image src={file.preview} alt={file.name} boxSize='50px' objectFit='contain' />
                          )}
                          {file.type.includes('json') && <Text fontWeight='600'>JSON</Text>}
                          {file.type.includes('pdf') && <Text fontWeight='600'>PDF</Text>}
                          {file.type.includes('text') && <Text fontWeight='600'>TXT</Text>}
                        </Td>
                        <Td>
                          <VStack spacing='2xs' align='start'>
                            {!isDownloadable && <Text>{file.name}</Text>}
                            {!!isDownloadable && (
                              <Link onClick={() => openFile(file)} isExternal>
                                {file.name}
                              </Link>
                            )}
                            <Text color='gray.400'>Size: {convertFileSize(file.size)}</Text>
                          </VStack>
                        </Td>
                        <Td w='35px'>
                          <CloseButton onClick={() => removeFile(file)} />
                        </Td>
                      </Tr>
                    ))}
                </Tbody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </VStack>
    </Box>
  );
};
